import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Camera, DollarSign, 
  Check, Link, Store, Image
} from 'lucide-react';

const STEPS = ['Category', 'Photo', 'Details', 'Upload'];

const CATEGORIES = [
  { id: 'beauty', name: 'Beauty', icon: '💄', color: '#F9A8D4' },
  { id: 'clothing', name: 'Clothing', icon: '👕', color: '#7DD3FC' },
  { id: 'shoes', name: 'Shoes', icon: '👟', color: '#4ADE80' },
  { id: 'home', name: 'Home', icon: '🪑', color: '#FCD34D' },
  { id: 'tech', name: 'Tech', icon: '📱', color: '#A78BFA' },
  { id: 'travel', name: 'Travel', icon: '✈️', color: '#2DD4BF' },
  { id: 'gifts', name: 'Gifts', icon: '🎁', color: '#FB923C' },
  { id: 'flash', name: 'Flash Deals', icon: '⚡', color: '#F87171' },
];

const UploadPurchase = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state
  const [category, setCategory] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [productName, setProductName] = useState('');
  const [brand, setBrand] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [pricePaid, setPricePaid] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [notes, setNotes] = useState('');

  const selectedCategory = CATEGORIES.find(c => c.id === category);

  const canProceed = () => {
    switch (currentStep) {
      case 0: return category !== '';
      case 1: return imageUrl !== '';
      case 2: return productName !== '' && storeName !== '' && pricePaid !== '';
      case 3: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/verified-purchases', {
        category,
        product_name: productName,
        brand: brand || null,
        store_name: storeName,
        store_url: storeUrl || null,
        image_url: imageUrl,
        price_paid: parseFloat(pricePaid),
        original_price: originalPrice ? parseFloat(originalPrice) : null,
        notes: notes || null,
      });
      
      toast.success('Purchase uploaded! 📸 +20 points earned!');
      navigate(`/locker/${user.user_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload purchase');
    } finally {
      setLoading(false);
    }
  };

  const discount = originalPrice && pricePaid 
    ? Math.round(((parseFloat(originalPrice) - parseFloat(pricePaid)) / parseFloat(originalPrice)) * 100)
    : null;

  const savings = originalPrice && pricePaid 
    ? parseFloat(originalPrice) - parseFloat(pricePaid)
    : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 border-b transition-colors"
        style={{ 
          backgroundColor: selectedCategory ? `${selectedCategory.color}15` : 'transparent',
          borderColor: selectedCategory ? `${selectedCategory.color}30` : 'transparent'
        }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-heading font-bold flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Upload Purchase
            </span>
            <div className="w-10" />
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((step, index) => (
            <div key={step} className="flex items-center flex-1">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  index < currentStep 
                    ? 'text-black' 
                    : index === currentStep 
                      ? 'border-2' 
                      : 'bg-muted text-muted-foreground'
                }`}
                style={
                  index < currentStep 
                    ? { backgroundColor: selectedCategory?.color || '#F472B6' }
                    : index === currentStep 
                      ? { borderColor: selectedCategory?.color || '#F472B6', color: selectedCategory?.color || '#F472B6' }
                      : {}
                }
              >
                {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              {index < STEPS.length - 1 && (
                <div 
                  className="flex-1 h-0.5 mx-2 transition-all"
                  style={{ backgroundColor: index < currentStep ? (selectedCategory?.color || '#F472B6') : '#27272A' }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 pb-24">
        <AnimatePresence mode="wait">
          {/* Step 0: Category */}
          {currentStep === 0 && (
            <motion.div
              key="category"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">What did you buy?</h2>
                <p className="text-muted-foreground">Upload proof of your smart purchase</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map((cat) => (
                  <motion.button
                    key={cat.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCategory(cat.id)}
                    className={`p-6 rounded-2xl text-center transition-all border-2 ${
                      category === cat.id 
                        ? 'border-current shadow-lg' 
                        : 'border-transparent bg-card hover:border-border'
                    }`}
                    style={category === cat.id ? { 
                      borderColor: cat.color,
                      backgroundColor: `${cat.color}20`
                    } : {}}
                    data-testid={`cat-${cat.id}`}
                  >
                    <span className="text-4xl mb-3 block">{cat.icon}</span>
                    <span className="font-medium">{cat.name}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 1: Photo */}
          {currentStep === 1 && (
            <motion.div
              key="photo"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">Show your purchase</h2>
                <p className="text-muted-foreground">Add a photo to verify and build trust</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Photo URL *</Label>
                  <div className="relative">
                    <Image className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      className="h-12 pl-11 bg-input/50 border-transparent focus:border-primary rounded-xl"
                      data-testid="image-url"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tip: Upload your photo to Imgur or similar and paste the link
                  </p>
                </div>

                {imageUrl && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-2xl overflow-hidden bg-muted aspect-square max-w-sm mx-auto"
                  >
                    <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
                  </motion.div>
                )}

                {!imageUrl && (
                  <div className="rounded-2xl border-2 border-dashed border-border p-12 text-center">
                    <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Your purchase photo will appear here</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 2: Details */}
          {currentStep === 2 && (
            <motion.div
              key="details"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">Purchase details</h2>
                <p className="text-muted-foreground">Tell us about your smart buy</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Product Name *</Label>
                  <Input
                    placeholder="Nike Air Max 90"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    className="h-12 bg-input/50 border-transparent focus:border-primary rounded-xl"
                    data-testid="product-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Brand</Label>
                  <Input
                    placeholder="Nike"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className="h-12 bg-input/50 border-transparent focus:border-primary rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Where did you buy it? *</Label>
                  <div className="relative">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      placeholder="Nike.com, Amazon, Target..."
                      value={storeName}
                      onChange={(e) => setStoreName(e.target.value)}
                      className="h-12 pl-11 bg-input/50 border-transparent focus:border-primary rounded-xl"
                      data-testid="store-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Store Link (optional)</Label>
                  <div className="relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={storeUrl}
                      onChange={(e) => setStoreUrl(e.target.value)}
                      className="h-12 pl-11 bg-input/50 border-transparent focus:border-primary rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Price Paid *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="89.99"
                        value={pricePaid}
                        onChange={(e) => setPricePaid(e.target.value)}
                        className="h-12 pl-9 bg-input/50 border-transparent focus:border-primary rounded-xl"
                        data-testid="price-paid"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Original Price</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="150.00"
                        value={originalPrice}
                        onChange={(e) => setOriginalPrice(e.target.value)}
                        className="h-12 pl-9 bg-input/50 border-transparent focus:border-primary rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                {savings !== null && savings > 0 && (
                  <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="p-4 rounded-xl text-center"
                    style={{ backgroundColor: `${selectedCategory?.color}20` }}
                  >
                    <p className="text-sm text-muted-foreground mb-1">You saved</p>
                    <span className="text-3xl font-bold" style={{ color: selectedCategory?.color }}>
                      ${savings.toFixed(2)}
                    </span>
                    {discount > 0 && (
                      <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 text-sm font-medium">
                        -{discount}%
                      </span>
                    )}
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Review & Upload */}
          {currentStep === 3 && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">Ready to share!</h2>
                <p className="text-muted-foreground">Add any notes and upload</p>
              </div>

              <Textarea
                placeholder="Any tips or thoughts about this purchase... (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[80px] bg-input/50 border-transparent focus:border-primary rounded-xl resize-none"
              />

              {/* Preview Card */}
              <div 
                className="rounded-2xl overflow-hidden border-2"
                style={{ borderColor: `${selectedCategory?.color}40` }}
              >
                <div className="aspect-square max-w-xs mx-auto bg-muted">
                  <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium text-black"
                      style={{ backgroundColor: selectedCategory?.color }}
                    >
                      {selectedCategory?.icon} {selectedCategory?.name}
                    </span>
                    <span className="text-xs text-muted-foreground">from {storeName}</span>
                  </div>
                  <h3 className="font-semibold mb-1">{productName}</h3>
                  {brand && <p className="text-sm text-muted-foreground mb-2">{brand}</p>}
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold" style={{ color: selectedCategory?.color }}>
                      ${pricePaid}
                    </span>
                    {originalPrice && (
                      <span className="text-sm text-muted-foreground line-through">${originalPrice}</span>
                    )}
                    {discount > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 text-xs font-medium">
                        -{discount}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Points Preview */}
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">You'll earn</p>
                <span className="text-2xl font-bold text-green-500">+20 points</span>
                <p className="text-xs text-muted-foreground mt-1">
                  +30 more if the community verifies it!
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 glass border-t border-border/50 p-4">
        <div className="max-w-2xl mx-auto flex gap-4">
          <Button 
            variant="outline"
            onClick={handleBack}
            className="flex-1 h-12 rounded-xl"
            data-testid="prev-btn"
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          {currentStep < STEPS.length - 1 ? (
            <Button 
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1 h-12 rounded-xl text-black transition-all"
              style={{ backgroundColor: selectedCategory?.color || '#F472B6' }}
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className="flex-1 h-12 rounded-xl text-black font-bold transition-all"
              style={{ backgroundColor: selectedCategory?.color || '#F472B6' }}
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Camera className="w-5 h-5 mr-2" />
                  Upload
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPurchase;
